import * as m from "~/paraglide/messages.js";

/**
 * One small, static product fragment per capability. Each is drawn from a
 * system Lindevo actually runs (the workbench, the lead radar, the agent team,
 * the content engine, the gateway and infrastructure) and carries the same
 * "illustrative" label as the rest of the site's examples.
 *
 * Every mock has one `data-tone="person"` row, the step that waits for a human,
 * drawn in the same pending tone the workbench uses for "Needs your approval".
 */

function MockFrame({
  children,
  title,
}: Readonly<{ children: React.ReactNode; title: string }>) {
  return (
    <figure className="cap-mock">
      <figcaption className="cap-mock-top">
        <span>{title}</span>
        <span className="cap-mock-note">{m.cap_illustrative()}</span>
      </figcaption>
      {children}
    </figure>
  );
}

export function SaveTimeMock() {
  const rows = [
    [m.cap_save_mock_1(), m.cap_save_mock_1_state(), "ready"],
    [m.cap_save_mock_2(), m.cap_save_mock_2_state(), "done"],
    [m.cap_save_mock_3(), m.cap_save_mock_3_state(), "person"],
  ] as const;

  return (
    <MockFrame title={m.cap_save_mock_title()}>
      <ul className="cap-rows">
        {rows.map(([subject, state, tone]) => (
          <li key={subject}>
            <span>{subject}</span>
            <span className="cap-chip" data-tone={tone}>
              {state}
            </span>
          </li>
        ))}
      </ul>
    </MockFrame>
  );
}

export function MakeMoneyMock() {
  const rows = [
    [m.cap_money_mock_1(), 91, m.cap_money_mock_1_state(), "person"],
    [m.cap_money_mock_2(), 78, m.cap_money_mock_2_state(), "running"],
    [m.cap_money_mock_3(), 64, m.cap_money_mock_3_state(), "ready"],
  ] as const;

  return (
    <MockFrame title={m.cap_money_mock_title()}>
      <table className="cap-table">
        <thead>
          <tr>
            <th scope="col">{m.cap_money_mock_col_company()}</th>
            <th scope="col">{m.cap_money_mock_col_fit()}</th>
            <th scope="col">{m.cap_money_mock_col_next()}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([company, fit, state, tone]) => (
            <tr key={company}>
              <td>{company}</td>
              <td>
                <span className="cap-fit">
                  <span style={{ width: `${fit}%` }} />
                </span>
                {fit}
              </td>
              <td>
                <span className="cap-chip" data-tone={tone}>
                  {state}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </MockFrame>
  );
}

export function AgentTeamMock() {
  const agents = [
    [m.cap_agents_mock_1(), m.cap_agents_mock_1_detail(), "€0.80"],
    [m.cap_agents_mock_2(), m.cap_agents_mock_2_detail(), "€1.20"],
    [m.cap_agents_mock_3(), m.cap_agents_mock_3_detail(), "€0.40"],
  ] as const;

  return (
    <MockFrame title={m.cap_agents_mock_title()}>
      <ol className="cap-chain">
        {agents.map(([role, detail, limit]) => (
          <li key={role}>
            <strong>{role}</strong>
            <span>{detail}</span>
            <span className="cap-limit">
              {m.cap_agents_mock_limit()} {limit}
            </span>
          </li>
        ))}
        <li data-tone="person">
          <strong>{m.cap_agents_mock_4()}</strong>
          <span>{m.cap_agents_mock_4_detail()}</span>
        </li>
      </ol>
    </MockFrame>
  );
}

export function ResearchMock() {
  const findings = [
    [m.cap_research_mock_1(), [1, 2]],
    [m.cap_research_mock_2(), [3]],
    [m.cap_research_mock_3(), [2, 4]],
  ] as const;

  return (
    <MockFrame title={m.cap_research_mock_title()}>
      <ul className="cap-brief">
        {findings.map(([claim, sources]) => (
          <li key={claim}>
            {claim}
            {sources.map((n) => (
              <sup className="cap-source" key={n}>
                {n}
              </sup>
            ))}
          </li>
        ))}
      </ul>
      <p className="cap-mock-foot">{m.cap_research_mock_foot()}</p>
    </MockFrame>
  );
}

export function FullSystemMock() {
  const layers = [
    [m.cap_full_mock_1(), m.cap_full_mock_1_detail()],
    [m.cap_full_mock_2(), m.cap_full_mock_2_detail()],
    [m.cap_full_mock_3(), m.cap_full_mock_3_detail()],
    [m.cap_full_mock_4(), m.cap_full_mock_4_detail()],
    [m.cap_full_mock_5(), m.cap_full_mock_5_detail()],
  ] as const;

  return (
    <MockFrame title={m.cap_full_label()}>
      <ol className="cap-stack">
        {layers.map(([layer, detail]) => (
          <li key={layer}>
            <strong>{layer}</strong>
            <span>{detail}</span>
          </li>
        ))}
      </ol>
    </MockFrame>
  );
}
